// The turn row: whose move it is, what the game is waiting on, and the three
// things a player can do that are not a move (PLAN-UI §28.3, U3).
//
// Reads `SessionView.turnRow` (PLAN-CLIENT-SPLIT day 8): the status word, the
// counters, the two refusals and the two options are the engine's to say.
// Undo is not an option — it is a log operation — so it goes through
// `game.play`. The room's status is the net client's and stays here.

import { useNet } from "src/client/ai/hooks/useNet";
import { TurnRow } from "../TurnRow";
import { HistoryTransportZone } from "./HistoryTransportZone";
import { PACES, type WatchTransport } from "../useWatchTransport";
import { useShellMode } from "../shell/ShellMode";
import type { BoardInteraction } from "../useBoardInteraction";
import { useGame } from "../useGame";
import { useSession, useSessionView } from "../useSessionView";

/**
 * The socket is between rooms: a request would land nowhere. The one undo
 * refusal the engine cannot know, since the room is the net client's — said
 * in the engine's own words (`UNDO_REFUSAL`), kept here by `turnRow.test.ts`.
 */
export const RECONNECTING = "Reconnecting to the room — try again in a moment.";

export function TurnRowZone({
  watch,
  onPause
}: {
  /** @deprecated Unread since day 8 — the zone reads the session's view. */
  board_?: BoardInteraction;
  /** Driving a game nobody is playing (`useWatchTransport`). */
  watch: WatchTransport;
  onPause: () => void;
}) {
  const game = useGame();
  const session = useSession();
  const { turnRow } = useSessionView();
  const { net } = useNet();
  const { scenario, watching, playing, onExit } = useShellMode();

  // What is being *played*, not what was picked: a run works up to its
  // difficulty, so the chip names the level the opponent is actually on.
  const level = game.catalog.difficulties().find((entry) => entry.id === playing)?.name ?? "";

  return (
    <TurnRow
      terminal={turnRow.terminal}
      label={turnRow.status}
      turn={turnRow.turn}
      // Single player only: mating the opponent opens the next front rather
      // than ending the game, so the stage number is the score.
      stage={scenario ? { number: turnRow.stage, level } : null}
      waveOwed={turnRow.waveOwed}
      spawnClock={turnRow.spawnClock}
      clockOwed={turnRow.clockOwed}
      net={net}
      watching={watching}
      // Undo: this turn's last action, in every mode (§29 U2, U3). Not a
      // command and never dispatched — it *rewrites* the log rather than
      // appending to it. In a room that rewrite belongs to the server, so
      // `play` is the one door: local store or request, decided there.
      undoRefusal={net.status === "connecting" ? RECONNECTING : turnRow.undoRefusal}
      onUndo={() => game.play({ kind: "undo" })}
      // End turn is legal for either side on its own turn, in a run and in
      // versus alike, and it is free. The refusals are the engine's: a
      // finished game, check, somebody else's clock, and looking back.
      endTurnRefusal={turnRow.endTurn.refusal}
      endTurnReady={turnRow.endTurn.ready}
      onPass={() => session.choose(turnRow.endTurn.option)}
      // The watch transport, in the slot the three turn actions vacate
      // (U8-A). A whole seat, not a control going quiet: nobody is playing,
      // so what replaces Undo and End turn is the way you drive the
      // game you are looking at.
      transport={
        watching
          ? {
              paused: watch.paused,
              onPause: watch.setPaused,
              onStep: () => watch.setStep((n) => n + 1),
              pace: watch.pace,
              paces: PACES,
              onPace: watch.setPace
            }
          : null
      }
      // First, back, forward and Latest (§29 U4), in the transport's group.
      history={<HistoryTransportZone />}
      onPause={onPause}
      onLeave={onExit}
    />
  );
}
